import { AlertTriangle, Copy, Smartphone } from "@tamagui/lucide-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useContext, useEffect, useRef, useState } from "react";
import { GestureResponderEvent, Platform } from "react-native";
import {
  type TamaguiElement,
  XStack,
  YStack,
  getTokenValue,
  useTheme,
} from "tamagui";

import { TextSendMessageRequestType as SendMessageRequestType } from "@/api/model";
import {
  AttachmentType,
  SendCuraiMessageVariables,
  useIsChatMutationPending,
  useSendCuraiMessage,
  useSendNoriMessage,
} from "@/api/mutations/conversation";
import AnimatedStrokeBorder from "@/components/animated/AnimatedStrokeBorder";
import Chip from "@/components/Chip";
import TimeTravelControl from "@/components/TimeTravelControl";
import { Link, Paragraph } from "@/components/Typography";
import { isDev, isLocal, isProd } from "@/config/env";
import MultiselectInputControl from "@/conversation/InputControl/Multiselect";
import SmsConsentBanner from "@/conversation/SmsConsentBanner";
import SmsTimeline from "@/conversation/sidebar/SmsTimeline";
import TestVisitPopover from "@/conversation/TestVisitPopover";
import {
  useCurrentConversationId,
  useCurrentConversationType,
  useHideTalkToDoctorButton,
  useNoriConversationCount,
} from "@/hooks/conversation";
import useHandleAttachmentsPicked from "@/hooks/useHandleAttachmentsPicked";
import { useIsSignedInStable } from "@/hooks/useIsSignedInStable";
import useMaybeCopyText from "@/hooks/useMaybeCopyText";
import { useAskUserQuestionsStore } from "@/store/askUserQuestions";
import { useConversationComposition } from "@/store/conversation";
import fonts from "@/theme/fonts";
import { getShadow } from "@/theme/helpers";
import ChatIcon from "./ChatIcon";
import ChatInputField from "./ChatInputField";
import { ConstrainedView } from "./Layout";
import { LayoutContext } from "./LayoutProvider";
import withTracking from "./withTracking";

const MAX_MESSAGE_LENGTH = 2000;

const SelectedChipsDisplay = ({
  chips,
  onPress,
}: {
  chips: string[];
  onPress: (chip: string) => void;
}) => {
  if (!chips.length) {
    return null;
  }
  return (
    <XStack
      gap="$150"
      paddingHorizontal="$200"
      paddingTop="$200"
      flexWrap="wrap"
    >
      {chips.map((chip) => (
        <Chip selected key={chip} text={chip} onPress={() => onPress(chip)} />
      ))}
    </XStack>
  );
};

const Disclaimer = ({
  conversationId,
  conversationType,
  onCopy,
}: {
  conversationId?: string;
  conversationType?: string | null;
  onCopy: (text: string) => void;
}) => {
  const { setDevSidebarContent } = useContext(LayoutContext);
  if (!conversationId || conversationType === "curai") {
    return null;
  }
  return (
    <XStack
      marginTop="$200"
      justifyContent="center"
      alignItems="center"
      gap="$100"
    >
      {!isProd && (
        <XStack position="absolute" left={0} gap="$50">
          <ChatIcon
            active={false}
            Icon={Copy}
            ariaLabel="Copy conversation id"
            onPress={() => onCopy(conversationId)}
          />
          {(isDev || isLocal) && (
            <ChatIcon
              active={false}
              Icon={Smartphone}
              ariaLabel="Open SMS view"
              onPress={() =>
                setDevSidebarContent(() => (props: any) => (
                  <SmsTimeline {...props} />
                ))
              }
            />
          )}
        </XStack>
      )}
      <AlertTriangle color="$iconBase" size="$150" />
      <Paragraph color="$textSecondary" size="$2xs">
        Not a replacement for a doctor. For emergencies, call 911.
      </Paragraph>
      {(isDev || isLocal) && (
        <XStack position="absolute" right={0} gap="$50">
          <TestVisitPopover conversationId={conversationId} />
          <TimeTravelControl />
        </XStack>
      )}
    </XStack>
  );
};

const TermsOfUse = () => {
  // Stop TOS and Privacy Policy links from triggering the onFocus event in ChatInput
  const stopPropagation = (e: GestureResponderEvent) => {
    e.stopPropagation();
  };

  return (
    <Paragraph textAlign="center" size="$xs" color="$textPrimary">
      By messaging, you agree to our{" "}
      <Link size="$xs" href="/terms" bold onPress={stopPropagation}>
        Terms
      </Link>{" "}
      and{" "}
      <Link size="$xs" href="/privacy" bold onPress={stopPropagation}>
        Privacy Policy
      </Link>
      .
    </Paragraph>
  );
};

const ChatInput = ({
  onSubmitEditing,
  onFocus,
  enableGradient,
  constrained,
  inputRef: externalInputRef,
  animatedStroke: animatedStrokeProp = false,
  placeholder = "Send message",
  ...props
}: {
  constrained: boolean;
  onSubmitEditing: () => void;
  onFocus: () => void;
  enableGradient: boolean;
  inputRef?: React.RefObject<TamaguiElement | null>;
  animatedStroke?: boolean;
  placeholder?: string;
}) => {
  const internalInputRef = useRef<TamaguiElement>(null);
  const inputRef = externalInputRef || internalInputRef;
  const stopAndSendRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const [attachments, setAttachments] = useState<AttachmentType[]>([]);
  const conversationId = useCurrentConversationId();
  const isSignedIn = useIsSignedInStable();
  const {
    text,
    setText,
    inputControl,
    isRecording,
    pressChip,
    deleteLastChip,
    clearInputControl,
    attachmentCount,
    attachmentSize,
    setAttachmentCount,
    setAttachmentSize,
  } = useConversationComposition();
  const [height, setHeight] = useState<number | undefined>(
    Platform.OS === "web" ? fonts["sans-serif"].lineHeight.md : undefined,
  );
  const theme = useTheme();
  const { clearSession: clearAskUserQuestions } = useAskUserQuestionsStore();

  useEffect(() => {
    return () => {
      clearInputControl();
      clearAskUserQuestions();
    };
  }, [clearInputControl, clearAskUserQuestions]);

  const maybeCopy = useMaybeCopyText();
  const { data: conversationType, isPending: conversationTypePending } =
    useCurrentConversationType();
  const noriConversationCount = useNoriConversationCount();
  const { sendNoriMessage } = useSendNoriMessage();
  const { mutate: sendCuraiMessage } = useSendCuraiMessage();
  const { handleAttachmentsPicked, isUploading } = useHandleAttachmentsPicked({
    setAttachments,
    attachmentCount,
    attachmentSize,
    setAttachmentCount,
    setAttachmentSize,
  });
  const isPending = useIsChatMutationPending();
  const { setChatInputHeight, initialChatInputHeight, lg } =
    useContext(LayoutContext);
  const showVoiceRecording = conversationType !== "curai";
  const shouldDisableTextInput =
    (!!conversationId && conversationTypePending) ||
    (inputControl?.type === "suggestions" && inputControl.lockInput === true);
  const hideTalkToDoctorButton = useHideTalkToDoctorButton();

  const resizeChatInput = () => {
    if (Platform.OS === "web") {
      window.requestAnimationFrame(() => {
        // On web the TextArea ref resolves to the underlying <textarea>; reset
        // then grow to scrollHeight so the field auto-sizes to its content.
        const el = inputRef.current as unknown as HTMLTextAreaElement | null;
        if (el) {
          el.style.height = "0px";
          el.style.height = `${el.scrollHeight}px`;
        }
      });
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies:conversationId Reset the initial height when convo id changes--the input has a different natural height on landing page vs. conversation page
  useEffect(() => {
    return () => {
      initialChatInputHeight.current = 0;
    };
  }, [initialChatInputHeight, conversationId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: needed to handle dictation appropriately
  useEffect(() => {
    resizeChatInput();
  }, [text]);

  const sendNoriMessageWrapper = async (
    overrideMessage?: string,
    type?: SendMessageRequestType,
  ) => {
    if (shouldDisableTextInput) {
      return;
    }
    // Don't allow interruption of an inbound streaming response
    if (isPending) {
      return;
    }
    onSubmitEditing?.();

    if (isRecording && stopAndSendRef.current) {
      // Stop recording and send voice message via ref
      await stopAndSendRef.current();
      return;
    }

    let inputAsString = overrideMessage ?? text;
    if (!overrideMessage && inputControl?.type === "multiple_choice") {
      inputAsString = inputControl.selected.join(", ");
      if (text) {
        const connector = !!inputAsString ? ", " : "";
        inputAsString += `${connector}${text}`;
      }
    }
    const finalAttachmentIds = attachments
      .map((a) => a.attachmentId)
      .filter((id): id is string => !!id);
    sendNoriMessage(inputAsString, {
      attachmentIds: finalAttachmentIds,
      attachments,
      type,
    });
    if (finalAttachmentIds.length > 0) {
      setAttachments([]);
    }
    resizeChatInput();
  };

  const sendTalkToDoctorMessage = () => {
    sendNoriMessageWrapper("", SendMessageRequestType.talk_to_doctor);
  };

  const sendCuraiMessageWrapper = async () => {
    if (shouldDisableTextInput) return;
    if (!conversationId) return;

    const finalAttachmentIds = attachments
      .map((a) => a.attachmentId)
      .filter((id): id is string => !!id);

    const trimmedMessage = text?.trim?.();
    if (!trimmedMessage && finalAttachmentIds.length === 0) return;
    onSubmitEditing?.();
    setText("");
    const data = {
      message: trimmedMessage || undefined,
      attachment_ids: finalAttachmentIds.length
        ? finalAttachmentIds
        : undefined,
    };
    sendCuraiMessage({
      conversationId,
      data,
      attachments,
      // note: slightly unsafe casting here in order to pass attachments variable to the mutation function,
      // needed for the optimistic update, not to pass it to the backend (auto-generated signature does not
      // include `attachments` parameter)
    } as SendCuraiMessageVariables);
    if (finalAttachmentIds.length > 0) {
      setAttachments([]);
    }
    resizeChatInput();
  };

  const sendMessageWrapper = () => {
    if (conversationType === "curai") {
      sendCuraiMessageWrapper();
    } else {
      sendNoriMessageWrapper();
    }
  };

  const handleKeyEvent = (
    key: string,
    shiftKey: boolean,
    preventDefault: () => void,
  ) => {
    if (key === "Backspace" && text.length === 0) {
      deleteLastChip();
    } else if (key === "Enter" && ((lg && !shiftKey) || (!lg && shiftKey))) {
      preventDefault();
      sendMessageWrapper();
    }
  };

  const selectedChips =
    inputControl?.type === "multiple_choice" ? inputControl.selected : [];

  const hasPendingAttachments = attachments.some((a) => !a.attachmentId);
  const hasContentToSend =
    !!text?.trim() || selectedChips.length > 0 || attachments.length > 0;
  const buttonDisabled =
    shouldDisableTextInput ||
    isPending ||
    (attachments.length > 0 && hasPendingAttachments) ||
    (!text?.trim?.() &&
      selectedChips.length === 0 &&
      attachments.length === 0 &&
      !isRecording);

  const showAnimatedStroke =
    animatedStrokeProp && Platform.OS === "web" && conversationType !== "curai";

  const shadowProps =
    conversationType === "curai"
      ? {
          borderWidth: "$strokeWidth",
          borderColor: "$strokeNeutral",
        }
      : {
          borderWidth: "$strokeWidth",
          ...getShadow({ y: 10, blur: "40px" }),
          borderColor: showAnimatedStroke ? "transparent" : "$strokeNeutral",
        };

  const content = (
    <YStack
      {...(!constrained && {
        alignSelf: "center",
        width: "100%",
      })}
      onPress={() => {
        if (shouldDisableTextInput) return;
        inputRef.current?.focus();
      }}
      marginBottom="$200"
      {...props}
      {...(shouldDisableTextInput ? { opacity: 0.6 } : {})}
    >
      <MultiselectInputControl />
      <YStack
        onLayout={(e) => {
          const height = e.nativeEvent.layout.height;
          if (height === 0) return;
          if (!initialChatInputHeight.current) {
            initialChatInputHeight.current = height;
          }
          setChatInputHeight(height);
        }}
      >
        {enableGradient && conversationType !== "curai" && (
          <LinearGradient
            colors={["transparent", theme.backgroundFill.get()]}
            locations={[0, 0.1]}
            start={{ x: 0, y: 0 }}
            end={{ x: 0, y: 1 }}
            style={{
              position: "absolute",
              marginVertical: getTokenValue("$-100"),
              top: 0,
              bottom: 0,
              width: "100%",
              pointerEvents: "none",
            }}
          />
        )}
        <AnimatedStrokeBorder enabled={showAnimatedStroke} radius="$150">
          <YStack borderRadius="$150" {...shadowProps} overflow="hidden">
            <SmsConsentBanner />
            <YStack
              backgroundColor={
                shouldDisableTextInput ? "$backgroundGround" : "$fillInput"
              }
            >
              <SelectedChipsDisplay chips={selectedChips} onPress={pressChip} />
              <ChatInputField
                attachments={attachments}
                onRemoveAttachment={(localUri) => {
                  const attachmentToRemove = attachments.find(
                    (a) => a.localUri === localUri,
                  );
                  if (attachmentToRemove) {
                    setAttachmentCount(attachmentCount - 1);
                    setAttachmentSize(
                      attachmentSize - (attachmentToRemove.fileSize || 0),
                    );
                  }
                  setAttachments((prev) =>
                    prev.filter((a) => a.localUri !== localUri),
                  );
                }}
                onAttachmentsPicked={handleAttachmentsPicked}
                isUploading={isUploading}
                inputRef={inputRef}
                stopAndSendRef={stopAndSendRef}
                showVoiceRecording={showVoiceRecording}
                conversationType={conversationType}
                text={text}
                setText={setText}
                height={height}
                placeholder={selectedChips.length ? undefined : placeholder}
                onFocus={onFocus}
                onSubmitEditing={sendMessageWrapper}
                onTalkToDoctor={sendTalkToDoctorMessage}
                onContentSizeChange={(e) => {
                  if (Platform.OS !== "web") {
                    // onContentSizeChange works simply on native, but not for web :(
                    setHeight(e.nativeEvent.contentSize.height);
                  }
                }}
                onKey={handleKeyEvent}
                buttonDisabled={buttonDisabled}
                isPending={
                  isPending || (attachments.length > 0 && hasPendingAttachments)
                }
                maxLength={MAX_MESSAGE_LENGTH}
                isDisabled={shouldDisableTextInput}
                noriConversationCount={noriConversationCount ?? 0}
                hideTalkToDoctorButton={hideTalkToDoctorButton}
                hasContentToSend={hasContentToSend}
              />
            </YStack>
          </YStack>
        </AnimatedStrokeBorder>
        {!conversationId && !isSignedIn && (
          <XStack marginTop="$150" alignSelf="center">
            <TermsOfUse />
          </XStack>
        )}
        <Disclaimer
          conversationId={conversationId}
          conversationType={conversationType}
          onCopy={maybeCopy}
        />
      </YStack>
    </YStack>
  );

  return constrained ? <ConstrainedView>{content}</ConstrainedView> : content;
};

export default withTracking(ChatInput, "textArea");
